<template>
  <div
    ref="chartNode"
    style="min-width: '100wv'; height 250px"
    :id="CHART_ID"
  ></div>
</template>

<script setup>
import { onMounted, nextTick, inject, ref } from 'vue';

import { D3LinearChart } from '@/d3/D3LinearChart';

import {
  formatDateHelper,
  formatTimeHelper,
  myRoundNumber_helper,
} from '@/helpers/main_helper';

const props = defineProps({
  object: { type: Object, required: true },
  interval: { type: Object, required: true },
  sumMonth: { type: Boolean, default: false },
});

const CHART_ID = 'excavator-operation-total-chart' + props.object.id;
const MIN_CHART_WIDTH = 550;
const MIN_CHART_HEIGHT = 250;

const toHours = inject('toHours');
const getFullDate = inject('getFullDate');

const chartNode = ref('chartNode');
const chart = ref(null);

onMounted(async () => {
  await nextTick();
  createChart();
});

function createChart() {
  const datasets = getDatasets(props.object);

  const { labels, engine_time, active_time, hirpm_notactime } = datasets;

  const minMax = getMinMaxByInterval();

  const { width, height } = getChartSize();

  const getLabel = (index) => {
    const label = props.sumMonth ? formatDateHelper(labels[index], 'mm.yy') : getFullDate(+labels[index] / 1000);
    const engine_timeVal = myRoundNumber_helper(engine_time[index], 1);
    const active_timeVal = myRoundNumber_helper(active_time[index], 1);
    const hirpm_notactimeVal = myRoundNumber_helper(hirpm_notactime[index], 1);
    // const retardertmprVal = myRoundNumber_helper(retardertmpr[index], 1);

    return `
            Дата: ${label}<br>
            Время работы ДВС: ${engine_timeVal} час.<br>
            Время активной работы ${active_timeVal} час.<br>
            Обороты более 1000 об/мин без активной работы: ${hirpm_notactimeVal} час.
          `;
  };
  const config = {
    width,
    height,
    isLegend: true,
    legendTextSize: '14px',
    legendRectWidth: 15,
    infoTitleSize: '14px',
    infoTitleBgFill: 'rgba(211, 211, 211,0.8)',
    dataTextSize: '14px',
    dataTextFontWeight: 'bold',
    margin: {
      top: 20,
      left: props.sumMonth ? 60 : 50,  
      bottom: 60,
      right: 100,
    },
    charts: [
      {
        type: 'bar',
        data: engine_time?.map((val, index) => {
          const tooltipHtml = getLabel(index);
          return {
            x: labels[index],
            y: val,
            tooltipHtml,
          };
        }) || [],
        colorStroke: '#0000ff',
        strokeWidth: 1,
        opacity: 0.75,
        label: 'Работа ДВС:',
        barWidth: props.sumMonth ? 20 : 10, 
        legendValueFormat: ({ data, index }) => {
          const { y: value = null } = data[index] ?? {};
          if (value === null || isNaN(value)) {
            return ' -';
          }
          return myRoundNumber_helper(value, 1) + ' (час)';
        },
        xAxis: 'x',
        yAxis: 'y',
        isDot: false,
        isLine: false,
        isBar: true,
        labelWidth: 120,
        legendValueSymbolsCount: 8,
      },
      {
        type: 'line',
        data: active_time?.map((val, index) => ({
          x: labels[index],
          y: val,
        })) || [],
        colorStroke: '#013220',
        strokeWidth: 2,
        opacity: 0.75,
        label: 'Активная работа (по инклинометру):',
        legendValueFormat: ({ data, index }) => {
          const { y: value = null } = data[index] ?? {};
          if (value === null || isNaN(value)) {
            return ' -';
          }
          return myRoundNumber_helper(value, 1) + ' (час)';
        },
        xAxis: 'x',
        yAxis: 'y',
        isDot: true,
        isLine: true,
        isBezierCurve: true,
        legendValueSymbolsCount: 8,
      },
      {
        type: 'line',
        data: hirpm_notactime?.map((val, index) => {
          return {
            x: labels[index],
            y: val
          };
        }) || [],
        colorStroke: '#ff0000',
        strokeWidth: 2,
        opacity: 0.75,
        label: 'Обороты более 1000 об/мин без активной работы:',
        legendValueFormat: ({ data, index }) => {
          const { y: value = null } = data[index] ?? {};
          if (value === null || isNaN(value)) {
            return ' -';
          }
          return myRoundNumber_helper(value, 1) + ' (час)';
        },
        xAxis: 'x',
        yAxis: 'y',
        isDot: true,
        isLine: true,
        isBezierCurve: true,
        legendValueSymbolsCount: 8,
      },
      // {
      //   type: 'line', 
      //   data: retardertmpr?.map((val, index) => {
      //     return {
      //       x: labels[index],
      //       y: val
      //     };
      //   }) || [],
      //   colorStroke: '#000000',
      //   strokeWidth: 1,
      //   opacity: 0.75,
      //   label: 'Температура гидравлического масла:',
      //   legendValueFormat: ({ data, index }) => {
      //     const { y: value = null } = data[index] ?? {};
      //     if (value === null || isNaN(value)) {
      //       return ' -';
      //     }
      //     return myRoundNumber_helper(value, 1) + ' (час)';
      //   },
      //   xAxis: 'x',
      //   yAxis: 'y',
      //   isDot: false,
      //   isLine: true,
      //   isBezierCurve: true,
      //   legendValueSymbolsCount: 8,
      // },
    ],
    axes: [
      {
        id: 'x',
        label: 'Дата / время',
        min: minMax.min,
        max: minMax.max,
        isTicks: true,
        isTime: true,
        tickValues: labels,
        tickFormat: (date) => {
          if (date.getHours() || date.getMinutes() || date.getSeconds()) {
            return formatDateHelper(date, 'hh:nn');
          }

          if (props.sumMonth) {
            return formatDateHelper(date, 'mm.yy');
          }

          return formatDateHelper(date, 'dd.mm.yy');
        },
        titleFormat: (date) => {
          if (props.sumMonth) {
            return formatDateHelper(date, 'mm.yy');
          }
          return formatDateHelper(date, 'hh:nn:ss dd.mm.yy');
        },
        titleIntervalFormat: (milliseconds) => {
          if (props.sumMonth) {
            return formatTimeHelper(milliseconds / 86400000, 'mm.yy');
          }
          return formatTimeHelper(milliseconds / 86400000, 'dd - hh:nn:ss');
        },
      },
      {
        id: 'y',
        label: 'Часы',
        orientText: 'left',
        orientScale: 'left',
        isTicks: true,
        isGrids: false,
      },
    ],
  };

  chart.value = new D3LinearChart({
    d3,
    elementId: CHART_ID,
    config,
    eventsHandling: false,
    clearAllButton: false,
  });
}

function getMinMaxByInterval() {
  const sideOffset = 5 * 3600 * 1000;
  const minMax = {
      min: new Date(props.interval.from - sideOffset),
      max: new Date(props.interval.to - 86400000 + sideOffset),
    };
  return minMax;
}

function getChartSize() {
  let width;
  
  if (props.sumMonth) {
    const periodCount = getPeriodCount(props.object, props.sumMonth);
    width = periodCount * 50;
  } else {
    width = (props.interval.to - props.interval.from) / 86400000;
    width *= 50;
  }

  if (width < MIN_CHART_WIDTH) width = MIN_CHART_WIDTH;

  let height = 0.4 * document.documentElement.clientHeight;
  if (height < MIN_CHART_HEIGHT) height = MIN_CHART_HEIGHT;

  return { width, height };
}

function getPeriodCount(object, sumMonth) {
  const { periods } = object;
  
  if (sumMonth) {
    const monthSet = new Set();
    
    periods.forEach((period) => {
      const date = new Date(period.TimeBegin);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthSet.add(monthKey);
    });
    
    return monthSet.size;
  } else {
    return periods.length;
  }
}

function getDatasets(object) {
  const { periods } = object;

  if (props.sumMonth) {
    const sortedPeriods = [...periods].sort((a, b) => new Date(a.TimeBegin) - new Date(b.TimeBegin));
    
    const monthlyData = {};
    
    sortedPeriods.forEach((period) => {
      const { TimeBegin, webSumm } = period;
      const date = new Date(TimeBegin);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          date: new Date(date.getFullYear(), date.getMonth(), 1),
          engine_time: 0,
          active_time: 0,
          hirpm_notactime: 0,
          // retardertmpr: 0,
        };
      }

      const { engine_time, active_time, hirpm_notactime } = webSumm;
      monthlyData[monthKey].engine_time += toHours(engine_time);
      monthlyData[monthKey].active_time += toHours(active_time);
      monthlyData[monthKey].hirpm_notactime += toHours(hirpm_notactime);
      // monthlyData[monthKey].retardertmpr += retardertmpr;
    });

    const result = {
      labels: [],
      engine_time: [],
      active_time: [],
      hirpm_notactime: [],
      // retardertmpr: [],
    };

    const sortedMonthKeys = Object.keys(monthlyData).sort();
    
    for (let i = 0; i < sortedMonthKeys.length; i++) {
      const monthKey = sortedMonthKeys[i];
      const monthData = monthlyData[monthKey];
      
      const [year, month] = monthKey.split('-');
      const monthBeginDate = new Date(year, month - 1, 1); 
      
      const intervalStartDate = new Date(props.interval.from);
      const monthEnd = new Date(year, month, 0);
      
      let labelDate;
      if (intervalStartDate <= monthBeginDate) {
        labelDate = monthBeginDate;
      } else if (intervalStartDate > monthBeginDate && intervalStartDate <= monthEnd) {
        labelDate = new Date(intervalStartDate);
      } else {
        labelDate = monthBeginDate;
      }
      
      result.labels.push(labelDate);
      result.engine_time.push(monthData.engine_time);
      result.active_time.push(monthData.active_time);
      result.hirpm_notactime.push(monthData.hirpm_notactime);
      // result.retardertmpr.push(monthData.retardertmpr);
    }

    return result;
  } else {
    const result = {
      labels: [],
      engine_time: [],
      active_time: [],
      hirpm_notactime: [],
      // retardertmpr: [],
    };

    periods.forEach((period, index) => {
      const { TimeBegin, webSumm } = period;
      const { engine_time, active_time, hirpm_notactime } = webSumm;

      result.labels[index] = new Date(TimeBegin);
      result.engine_time[index] = toHours(engine_time);
      result.active_time[index] = toHours(active_time);
      result.hirpm_notactime[index] = toHours(hirpm_notactime);
      // result.retardertmpr[index] = toHours(retardertmpr);
    });

    return result;
  }
}
</script>
